import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

function useFilterSelectors() {
  const [searchParams, setSearchParams] = useSearchParams();

  const onSingleFilterSelected = useCallback(
    (key: string, value: string) =>
      setSearchParams((prevValue) => {
        const newValue = new URLSearchParams(prevValue.toString());
        newValue.set(key, value);
        return newValue;
      }),
    [setSearchParams]
  );

  const onMultiFilterSelected = useCallback(
    (key: string, value: Array<string>) =>
      setSearchParams((prevValue) => {
        const newValue = new URLSearchParams(prevValue.toString());
        newValue.delete(key);
        value.forEach((v) => newValue.append(key, v));
        return newValue;
      }),
    [setSearchParams]
  );

  const onCombinedFilterSelected = useCallback(
    (parameterMap: Record<string, string | Array<string>>) => {
      const newValue = new URLSearchParams();
      for (const keyValue of Object.entries(parameterMap)) {
        const [key, value] = keyValue;
        if (Array.isArray(value)) {
          value.forEach((v) => newValue.append(key, v));
        } else if (typeof value === 'string' && !!value) {
          newValue.set(key, value);
        }
      }
      setSearchParams(newValue);
    },
    [setSearchParams]
  );

  const onPartialFilterSelected = useCallback(
    (parameterMap: Record<string, string | Array<string>>) => {
      const newValue = new URLSearchParams(searchParams.toString());
      for (const keyValue of Object.entries(parameterMap)) {
        const [key, value] = keyValue;
        newValue.delete(key);
        if (Array.isArray(value)) {
          value.forEach((v) => newValue.append(key, v));
        } else if (typeof value === 'string' && !!value) {
          newValue.set(key, value);
        }
      }
      setSearchParams(newValue);
    },
    [searchParams, setSearchParams]
  );

  const onFiltersDeleted = useCallback(
    (...keys: string[]) =>
      setSearchParams((prevValue) => {
        const newValue = new URLSearchParams(prevValue.toString());
        keys.forEach((key) => newValue.delete(key));
        return newValue;
      }),
    [setSearchParams]
  );

  return {
    onSingleFilterSelected,
    onMultiFilterSelected,
    onCombinedFilterSelected,
    onPartialFilterSelected,
    onFiltersDeleted,
  };
}

export default useFilterSelectors;
